import React, { useState, useEffect } from 'react';
import { 
  Settings,
  User,
  Bell,
  Shield,
  Palette,
  Database,
  Key,
  Save,
  Check,
  Globe,
  Moon,
  Sun,
  Mail,
  AlertCircle
} from 'lucide-react';
import { api } from '../utils/api';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('am_user') || '{}');
  
  // Settings state
  const [settings, setSettings] = useState({
    profile: {
      username: currentUser.username || '',
      email: '',
      phone: '',
      bio: ''
    },
    notifications: {
      emailNotifications: true,
      newSubscriberAlerts: true,
      weeklySummary: false,
      eventReminders: true
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    appearance: {
      theme: 'dark',
      accentColor: '#D4A574'
    },
    integrations: {
      emailjs: {
        enabled: false,
        serviceId: '',
        templateId: '',
        publicKey: ''
      },
      analytics: {
        enabled: false,
        trackingId: ''
      }
    }
  });

  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.settings.get();
      
      if (response.settings) {
        setSettings(prev => ({
          ...prev,
          ...response.settings,
          profile: {
            ...prev.profile,
            ...response.settings.profile
          },
          security: {
            ...prev.security,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }
        }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setPasswordError('');
      
      // Validate password if attempting to change it
      if (settings.security.newPassword) {
        if (!settings.security.currentPassword) {
          setPasswordError('Current password is required');
          return;
        }
        if (settings.security.newPassword !== settings.security.confirmPassword) {
          setPasswordError('New passwords do not match');
          return;
        }
        if (settings.security.newPassword.length < 6) {
          setPasswordError('Password must be at least 6 characters');
          return;
        }
      }
      
      // Prepare data to save (exclude sensitive fields)
      const dataToSave = {
        profile: settings.profile,
        notifications: settings.notifications,
        appearance: settings.appearance,
        integrations: settings.integrations
      };
      
      await api.settings.update(dataToSave);
      
      // Apply theme immediately
      if (settings.appearance.theme === 'light') {
        document.documentElement.classList.add('light-theme');
      } else {
        document.documentElement.classList.remove('light-theme');
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
      // Clear password fields
      setSettings(prev => ({
        ...prev,
        security: {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
      }));
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const updateNestedSettings = (category, subcategory, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          ...prev[category][subcategory],
          [field]: value
        }
      }
    }));
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'integrations', label: 'Integrations', icon: Database },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A574] mx-auto mb-4"></div>
          <p className="text-[#888888]">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light mb-2">Settings</h1>
          <p className="text-[#888888]">Manage your preferences and configurations</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 text-green-500">
            <Check size={20} />
            <span>Settings saved</span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 pb-6 border-b border-[#333333]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
              activeTab === tab.id
                ? 'bg-[#1A1A1A] text-[#D4A574] border border-[#333333]'
                : 'text-[#888888] hover:text-white hover:bg-[#1A1A1A]'
            }`}
          >
            <tab.icon size={18} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card-dark">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="text-xl font-light mb-4">Profile Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#888888] mb-2">Username</label>
                <input 
                  type="text" 
                  className="input-dark w-full" 
                  value={settings.profile.username}
                  disabled 
                />
              </div>
              <div>
                <label className="block text-sm text-[#888888] mb-2">Email</label>
                <input 
                  type="email" 
                  className="input-dark w-full" 
                  value={settings.profile.email}
                  onChange={(e) => updateSettings('profile', 'email', e.target.value)}
                  placeholder={`${currentUser.username}@art-officials.com`}
                />
              </div>
              <div>
                <label className="block text-sm text-[#888888] mb-2">Role</label>
                <input 
                  type="text" 
                  className="input-dark w-full" 
                  value={currentUser.name || 'Co-founder'}
                  disabled 
                />
              </div>
              <div>
                <label className="block text-sm text-[#888888] mb-2">Phone</label>
                <input 
                  type="tel" 
                  className="input-dark w-full" 
                  value={settings.profile.phone}
                  onChange={(e) => updateSettings('profile', 'phone', e.target.value)}
                  placeholder="+351..." 
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#888888] mb-2">Bio</label>
              <textarea 
                className="input-dark w-full" 
                rows="3" 
                value={settings.profile.bio}
                onChange={(e) => updateSettings('profile', 'bio', e.target.value)}
                placeholder="Tell us about yourself..."
              />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <h2 className="text-xl font-light mb-4">Notification Preferences</h2>
            <div className="space-y-4">
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-[#888888]">Receive updates about events and activities</p>
                </div>
                <input 
                  type="checkbox" 
                  className="toggle" 
                  checked={settings.notifications.emailNotifications}
                  onChange={(e) => updateSettings('notifications', 'emailNotifications', e.target.checked)}
                />
              </label>
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium">New Subscriber Alerts</p>
                  <p className="text-sm text-[#888888]">Get notified when someone joins the mailing list</p>
                </div>
                <input 
                  type="checkbox" 
                  className="toggle" 
                  checked={settings.notifications.newSubscriberAlerts}
                  onChange={(e) => updateSettings('notifications', 'newSubscriberAlerts', e.target.checked)}
                />
              </label>
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Weekly Summary</p>
                  <p className="text-sm text-[#888888]">Receive a weekly summary of activities</p>
                </div>
                <input 
                  type="checkbox" 
                  className="toggle"
                  checked={settings.notifications.weeklySummary}
                  onChange={(e) => updateSettings('notifications', 'weeklySummary', e.target.checked)}
                />
              </label>
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Event Reminders</p>
                  <p className="text-sm text-[#888888]">Get reminded about upcoming events</p>
                </div>
                <input 
                  type="checkbox" 
                  className="toggle" 
                  checked={settings.notifications.eventReminders}
                  onChange={(e) => updateSettings('notifications', 'eventReminders', e.target.checked)}
                />
              </label>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-6">
            <h2 className="text-xl font-light mb-4">Security Settings</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm text-[#888888] mb-2">Current Password</label>
                <input 
                  type="password" 
                  className="input-dark w-full"
                  value={settings.security.currentPassword}
                  onChange={(e) => updateSettings('security', 'currentPassword', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-[#888888] mb-2">New Password</label>
                  <input 
                    type="password" 
                    className="input-dark w-full"
                    value={settings.security.newPassword}
                    onChange={(e) => updateSettings('security', 'newPassword', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#888888] mb-2">Confirm Password</label>
                  <input 
                    type="password" 
                    className="input-dark w-full"
                    value={settings.security.confirmPassword}
                    onChange={(e) => updateSettings('security', 'confirmPassword', e.target.value)}
                  />
                </div>
              </div>
              {passwordError && (
                <p className="text-red-500 text-sm">{passwordError}</p>
              )}
              <div className="pt-4 border-t border-[#333333]">
                <h3 className="font-medium mb-4">Active Sessions</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Current Session</p>
                      <p className="text-xs text-[#888888]">Web Browser • Lisboa, Portugal</p>
                    </div>
                    <span className="text-xs text-green-500">Active now</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="space-y-6">
            <h2 className="text-xl font-light mb-4">Appearance Settings</h2>
            <div>
              <label className="block text-sm text-[#888888] mb-4">Theme</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <button
                  onClick={() => updateSettings('appearance', 'theme', 'dark')}
                  className={`p-4 rounded-lg border transition-all ${
                    settings.appearance.theme === 'dark' 
                      ? 'border-[#D4A574] bg-[#0A0A0A]' 
                      : 'border-[#333333] hover:border-[#444444]'
                  }`}
                >
                  <Moon className="mx-auto mb-2" size={24} />
                  <p className="text-sm">Dark</p>
                </button>
                <button
                  onClick={() => updateSettings('appearance', 'theme', 'light')}
                  className={`p-4 rounded-lg border transition-all ${
                    settings.appearance.theme === 'light' 
                      ? 'border-[#D4A574] bg-[#0A0A0A]' 
                      : 'border-[#333333] hover:border-[#444444]'
                  }`}
                >
                  <Sun className="mx-auto mb-2" size={24} />
                  <p className="text-sm">Light</p>
                </button>
                <button
                  onClick={() => updateSettings('appearance', 'theme', 'auto')}
                  className={`p-4 rounded-lg border transition-all ${
                    settings.appearance.theme === 'auto' 
                      ? 'border-[#D4A574] bg-[#0A0A0A]' 
                      : 'border-[#333333] hover:border-[#444444]'
                  }`}
                >
                  <Globe className="mx-auto mb-2" size={24} />
                  <p className="text-sm">System</p>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-[#888888] mb-2">Accent Color</label>
              <div className="flex gap-3">
                <button 
                  onClick={() => updateSettings('appearance', 'accentColor', '#2D6B42')}
                  className={`w-10 h-10 bg-[#2D6B42] rounded-lg ${
                    settings.appearance.accentColor === '#2D6B42' ? 'ring-2 ring-white' : ''
                  }`}
                />
                <button 
                  onClick={() => updateSettings('appearance', 'accentColor', '#D4A574')}
                  className={`w-10 h-10 bg-[#D4A574] rounded-lg ${
                    settings.appearance.accentColor === '#D4A574' ? 'ring-2 ring-white' : ''
                  }`}
                />
                <button 
                  onClick={() => updateSettings('appearance', 'accentColor', '#3B82F6')}
                  className={`w-10 h-10 bg-blue-500 rounded-lg ${
                    settings.appearance.accentColor === '#3B82F6' ? 'ring-2 ring-white' : ''
                  }`}
                />
                <button 
                  onClick={() => updateSettings('appearance', 'accentColor', '#8B5CF6')}
                  className={`w-10 h-10 bg-purple-500 rounded-lg ${
                    settings.appearance.accentColor === '#8B5CF6' ? 'ring-2 ring-white' : ''
                  }`}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <h2 className="text-xl font-light mb-4">Integrations</h2>
            <div className="space-y-4">
              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A1A] rounded-lg flex items-center justify-center">
                      <Key size={24} />
                    </div>
                    <div>
                      <p className="font-medium">Cloudflare KV</p>
                      <p className="text-sm text-[#888888]">Database storage for all data</p>
                    </div>
                  </div>
                  <span className="text-sm text-green-500">Connected</span>
                </div>
              </div>

              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A1A] rounded-lg flex items-center justify-center">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="font-medium">EmailJS</p>
                      <p className="text-sm text-[#888888]">Email service integration</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={settings.integrations.emailjs.enabled}
                      onChange={(e) => updateNestedSettings('integrations', 'emailjs', 'enabled', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4A574]"></div>
                  </label>
                </div>
                {settings.integrations.emailjs.enabled && (
                  <div className="space-y-3 pt-3 border-t border-[#333333]">
                    <input
                      type="text"
                      placeholder="Service ID"
                      className="input-dark w-full text-sm"
                      value={settings.integrations.emailjs.serviceId}
                      onChange={(e) => updateNestedSettings('integrations', 'emailjs', 'serviceId', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Template ID"
                      className="input-dark w-full text-sm"
                      value={settings.integrations.emailjs.templateId}
                      onChange={(e) => updateNestedSettings('integrations', 'emailjs', 'templateId', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Public Key"
                      className="input-dark w-full text-sm"
                      value={settings.integrations.emailjs.publicKey}
                      onChange={(e) => updateNestedSettings('integrations', 'emailjs', 'publicKey', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="p-4 bg-[#0A0A0A] rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1A1A1A] rounded-lg flex items-center justify-center">
                      <Database size={24} />
                    </div>
                    <div>
                      <p className="font-medium">Analytics</p>
                      <p className="text-sm text-[#888888]">Track event performance</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={settings.integrations.analytics.enabled}
                      onChange={(e) => updateNestedSettings('integrations', 'analytics', 'enabled', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4A574]"></div>
                  </label>
                </div>
                {settings.integrations.analytics.enabled && (
                  <div className="pt-3 border-t border-[#333333]">
                    <input
                      type="text"
                      placeholder="Tracking ID (e.g., G-XXXXXXXXXX)"
                      className="input-dark w-full text-sm"
                      value={settings.integrations.analytics.trackingId}
                      onChange={(e) => updateNestedSettings('integrations', 'analytics', 'trackingId', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-[#333333]">
          <button 
            onClick={handleSave} 
            className="btn-primary flex items-center gap-2"
            disabled={saving}
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;